import { forwardRef, useContext, useImperativeHandle, useRef, useState } from "react"
import CatalogBrowserContext, { ItemHandlerRef } from "../ItemCatalog/CatalogBrowserContext"
import { ItemsContext, LastAddedCatalogItemWithName, OnTemplateAdd } from "./ItemsRoot"
import { findWorkareaIdForWorkareaItem, getNewSortIndex, getServerState, getWorkareaId, needsVisitId } from "./catalogBrowserContextModel"
import { CatalogItem } from "../../domain/catalogItem"
import { CatalogTemplateWithItems } from "../../domain/catalogTemplate"
import { ActualItemOrigins, WorkareaItemTypes } from "../../common/constants"
import { viewItemForDraggableCatalogItem, viewItemForDraggableTemplate } from "./itemModel"
import WorkareaSelectionModal from "./WorkareaSelectionModal"
import WorkareaAndVisitSelectionModal from "./WorkareaAndVisitSelectionModal"

type Props = {
  onTemplateAdd: OnTemplateAdd
  setLastAddedCatalogItem: (lastAddedCatalogItem?: LastAddedCatalogItemWithName) => void
}

const ItemHandler = forwardRef((props: Props, ref) => {
  const { dispatch, focusedView, focusedWorkareaId, focusedReceiptId, jobItemsQuery, changeSet, jobData } = useContext(ItemsContext)

  const [pendingCatalogItem, setPendingCatalogItem] = useState<{ catalogItem: CatalogItem, toGuid?: string, name?: string } | undefined>()
  const [pendingTemplate, setPendingTemplate] = useState<{ template: CatalogTemplateWithItems, toGuid?: string } | undefined>()

  const containerRef = useRef<any>()

  const items = {
    estimated: jobItemsQuery.data?.estimatedItems ?? [],
    actual: jobItemsQuery.data?.actualItems ?? [],
    added: changeSet.addedItems,
    catalogItems: jobItemsQuery.data?.catalogItems ?? [],
    templateItems: jobItemsQuery.data?.templateItems ?? []
  }

  const addCatalogItem = (catalogItem: CatalogItem, toGuid?: string, selectedWorkareaId?: number, name?: string, visitId?: string) => {
    const workareaId = getWorkareaId(focusedView, focusedWorkareaId, selectedWorkareaId, jobData.workareas)

    if (workareaId === undefined || needsVisitId(focusedView, visitId)) {
      setPendingCatalogItem({ catalogItem, toGuid, name })
      return
    }

    const serverState = getServerState(workareaId, focusedView, items)
    const sortIndex = getNewSortIndex(workareaId, serverState, changeSet.addedItems)

    props.setLastAddedCatalogItem({ catalogItem, name })

    dispatch({
      type: 'ADD_ITEM_FROM_CATALOG',
      catalogItem,
      addItemParams: {
        quantity: 0,
        workareaId,
        workareas: jobData.workareas,
        toGuid,
        budgets: jobData.budgets,
        workareaItemType: focusedView === 'workarea-estimated' ? WorkareaItemTypes.Estimated : WorkareaItemTypes.Actual,
        sortIndex,
        origin: focusedView === 'receipt' ? ActualItemOrigins.Receipt : ActualItemOrigins.Web,
        serverState,
        visitId,
        receiptId: focusedView === 'receipt' ? focusedReceiptId : undefined,
        name
      }
    })
  }

  const addTemplate = (template: CatalogTemplateWithItems, toGuid?: string, selectedWorkareaId?: number, visitId?: string) => {
    const workareaId = getWorkareaId(focusedView, focusedWorkareaId, selectedWorkareaId, jobData.workareas)

    if (workareaId === undefined || needsVisitId(focusedView, visitId)) {
      setPendingTemplate({ template, toGuid })
      return
    }

    const serverState = getServerState(workareaId, focusedView, items)
    const sortIndex = getNewSortIndex(workareaId, serverState, changeSet.addedItems)

    dispatch({
      type: 'ADD_TEMPLATE_FROM_CATALOG',
      template,
      addItemParams: {
        quantity: 0,
        workareaId: workareaId,
        workareas: jobData.workareas,
        toGuid,
        budgets: jobData.budgets,
        workareaItemType: focusedView === 'workarea-estimated' ? WorkareaItemTypes.Estimated : WorkareaItemTypes.Actual,
        sortIndex,
        origin: focusedView === 'receipt' ? ActualItemOrigins.Receipt : ActualItemOrigins.Web,
        serverState,
        visitId,
        receiptId: focusedView === 'receipt' ? focusedReceiptId : undefined,
      }
    })

    props.onTemplateAdd(workareaId, template)
  }

  useImperativeHandle(ref, () => ({
    addCatalogItem: addCatalogItem,
    addTemplate: addTemplate,
    updateSorting: (fromGuid, toGuid) => {
      const workareaId = findWorkareaIdForWorkareaItem(fromGuid, items)
      const serverState = getServerState(workareaId, focusedView, items)
      dispatch({ type: 'UPDATE_SORTING', fromGuid, toGuid, serverState })
    },
    findOverlayItem: (guid) => {
      return changeSet.updatedItems.find(x => x.guid === guid)
        ?? changeSet.addedItems.find(x => x.guid === guid)
        ?? items.estimated.find(x => x.guid === guid)
        ?? items.actual.find(x => x.guid === guid)
    },
    getViewItemForDraggableCatalogItem: (catalogItem, dragId) => {
      return viewItemForDraggableCatalogItem(catalogItem, dragId)
    },
    getViewItemForDraggableTemplate: (template, dragId) => {
      return viewItemForDraggableTemplate(template, dragId)
    }
  } as ItemHandlerRef))

  return <div ref={containerRef}>
    <WorkareaSelectionModal
      open={!needsVisitId(focusedView) && (!!pendingCatalogItem || !!pendingTemplate)}
      workareas={jobData.workareas}
      onCancel={() => {
        setPendingCatalogItem(undefined)
        setPendingTemplate(undefined)
      }}
      onSelect={(workareaId) => {
        if (pendingTemplate) {
          addTemplate(pendingTemplate.template, pendingCatalogItem?.toGuid, workareaId)
        } else if (pendingCatalogItem) {
          addCatalogItem(pendingCatalogItem.catalogItem, pendingCatalogItem.toGuid, workareaId, pendingCatalogItem.name)
        }

        setPendingTemplate(undefined)
        setPendingCatalogItem(undefined)
      }}
      getContainer={() => containerRef.current}
    />
    <WorkareaAndVisitSelectionModal
      open={needsVisitId(focusedView) && (!!pendingCatalogItem || !!pendingTemplate)}
      workareas={jobData.workareas}
      visitData={jobData.visitData}
      onCancel={() => {
        setPendingCatalogItem(undefined)
        setPendingTemplate(undefined)
      }}
      onSelect={(workareaId, visitId) => {
        if (pendingTemplate) {
          addTemplate(pendingTemplate.template, pendingCatalogItem?.toGuid, workareaId, visitId)
        } else if (pendingCatalogItem) {
          addCatalogItem(pendingCatalogItem.catalogItem, pendingCatalogItem.toGuid, workareaId, pendingCatalogItem.name, visitId)
        }

        setPendingTemplate(undefined)
        setPendingCatalogItem(undefined)
      }}
      getContainer={() => containerRef.current}
    />
  </div>
})

const CatalogItemHandler = ({ onTemplateAdd, setLastAddedCatalogItem }: Props) => {
  const { itemHandlerRef } = useContext(CatalogBrowserContext)
  return <ItemHandler
    ref={itemHandlerRef}
    onTemplateAdd={onTemplateAdd}
    setLastAddedCatalogItem={setLastAddedCatalogItem}
  />
}

export default CatalogItemHandler
